import { ApiScenario, ApiScenarioLock, ApiSession } from '@/types';
import { HttpStatusCode } from 'axios';
import { mockApiScenarios, mockApiScenarioLocks, mockApiSessions } from '@/mocks';
import { quickLog } from '@/utils';
import { USE_MOCK_DATA } from '@/constants';
import authenticatedAxios from '@/lib/axios';

/**
 * Get scenario details including rubric and persona
 */
export const getScenarioById = async (scenarioId: number): Promise<ApiScenario> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock scenario by ID:', scenarioId);
    const scenario = mockApiScenarios.find((s) => s.id === scenarioId);
    if (!scenario) throw new Error(`Scenario with ID ${scenarioId} not found`);
    return scenario;
  }

  const endpoint = `app/scenarios/${scenarioId}`;
  const response = await authenticatedAxios.get<ApiScenario>(endpoint);
  return response.data;
};

/**
 * Get sessions (attempts) by user and optionally by scenario
 * @param userId User ID
 * @param scenarioId Optional scenario ID to filter by
 */
export const getSessions = async (userId: string, scenarioId?: number): Promise<ApiSession[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api(
      '[MOCK DATA] Returning mock sessions for user ID:',
      userId,
      'and scenario ID:',
      scenarioId,
    );

    let filteredSessions = mockApiSessions.filter((s) => s.owner_user_id === userId);
    if (scenarioId !== undefined) {
      filteredSessions = filteredSessions.filter((s) => s.scenario_id === scenarioId);
    }

    return filteredSessions;
  }

  const params = new URLSearchParams({ user_id: userId });
  if (scenarioId !== undefined) {
    params.append('scenario_id', scenarioId.toString());
  }

  const endpoint = `app/sessions/?${params}`;
  const response = await authenticatedAxios.get<ApiSession[]>(endpoint);
  return response.data;
};

/**
 * Get a specific session by ID
 */
export const getSessionById = async (sessionId: number): Promise<ApiSession> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock session by ID:', sessionId);
    const session = mockApiSessions.find((s) => s.id === sessionId);
    if (!session) throw new Error(`Session with ID ${sessionId} not found`);
    return session;
  }

  const endpoint = `app/sessions/${sessionId}`;
  const response = await authenticatedAxios.get<ApiSession>(endpoint);
  return response.data;
};

/**
 * Create a new session (start a roleplay)
 */
export const createSession = async (data: {
  enrollment_id: number;
  scenario_id: number;
  owner_user_id: string;
}): Promise<ApiSession> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Creating mock session with data:', data);
    const newSession: ApiSession = {
      id: Math.max(...mockApiSessions.map((s) => s.id)) + 1,
      ...data,
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      attempt_no: null,
      started_at: null,
      submitted_at: null,
    };

    mockApiSessions.push(newSession);
    return newSession;
  }

  const endpoint = 'app/sessions/';
  const response = await authenticatedAxios.post<ApiSession>(endpoint, data);
  return response.data;
};

/**
 * Update session status
 */
export const updateSessionStatus = async (
  sessionId: number,
  status: string,
  submitted_at?: string,
): Promise<ApiSession> => {
  if (USE_MOCK_DATA) {
    quickLog.api(
      '[MOCK DATA] Updating mock session status for session ID:',
      sessionId,
      'to status:',
      status,
    );

    const sessionIndex = mockApiSessions.findIndex((s) => s.id === sessionId);
    if (sessionIndex === -1) throw new Error(`Session with ID ${sessionId} not found`);

    mockApiSessions[sessionIndex] = {
      ...mockApiSessions[sessionIndex],
      status,
      updated_at: new Date().toISOString(),
    };

    return mockApiSessions[sessionIndex];
  }

  const endpoint = `app/sessions/${sessionId}/status`;
  const data = { status, submitted_at };
  const response = await authenticatedAxios.put<ApiSession>(endpoint, data);
  return response.data;
};

/**
 * Get user's locked scenarios
 */
export const getUserScenarioLocks = async (userId: string): Promise<ApiScenarioLock[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock scenario locks for user ID:', userId);
    return mockApiScenarioLocks.filter((lock) => lock.user_id === userId);
  }

  const endpoint = `app/scenarios/users/${userId}/locks`;
  const response = await authenticatedAxios.get<ApiScenarioLock[]>(endpoint);
  return response.data;
};

/**
 * Unlock Scenario For User
 */
export const unlockUserScenarioById = async (
  scenarioId: number,
  userId: string,
): Promise<HttpStatusCode> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Unlocking scenario for user ID and scenario ID:', userId, scenarioId);
    return 200;
  }

  const endpoint = `app/scenarios/${scenarioId}/users/${userId}/unlock`;
  const response = await authenticatedAxios.put<void>(endpoint);
  return response.status;
};
